/**
 * Comparatifs tab — sortable table grouped by offer/source/recruiter with
 * threshold-driven colour coding. Extracted from AnalyticsCalls.jsx
 * (iter29 refactor). Iso-behaviour.
 */
import { useEffect, useState } from "react";

import { api } from "../../lib/api";
import { AiCommentaryBlock } from "./AiCommentaryBlock";
import { buildQueryParams, EmptyBlock, LoadingBlock } from "./shared";

export function ComparatifsTab({ filters }) {
    const [groupBy, setGroupBy] = useState("offer");
    const [sortKey, setSortKey] = useState("volume");
    const [sortDir, setSortDir] = useState("desc");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get("/analytics/calls/comparatifs", { params: { ...buildQueryParams(filters), group_by: groupBy } })
            .then(({ data }) => alive && setData(data))
            .catch(() => alive && setData(null))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [filters, groupBy]);

    if (loading) return <LoadingBlock label="Chargement comparatifs…" />;
    if (!data) return <EmptyBlock label="Aucune donnée dans la période" />;

    const rows = [...data.rows].sort((a, b) => {
        const av = a[sortKey] ?? 0;
        const bv = b[sortKey] ?? 0;
        const dir = sortDir === "asc" ? 1 : -1;
        return (av === bv ? 0 : av > bv ? 1 : -1) * dir;
    });

    const toneClasses = {
        green: "bg-emerald-50 text-emerald-700 border-emerald-200",
        orange: "bg-amber-50 text-amber-700 border-amber-200",
        red: "bg-rose-50 text-rose-700 border-rose-200",
    };
    const dimLabels = { offer: "Offre", source: "Source", recruteur: "Chargé·e de recrutement" };
    const columns = [
        { key: "label", label: dimLabels[groupBy], align: "left" },
        { key: "volume", label: "Volume", align: "right" },
        { key: "reach_rate", label: "Décroché %", align: "right" },
        { key: "complete_rate", label: "Complétion %", align: "right" },
        { key: "qualify_rate", label: "Qualification %", align: "right" },
        { key: "closing_rate", label: "Closing %", align: "right" },
    ];
    const onSort = (key) => {
        if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(key); setSortDir("desc"); }
    };
    return (
        <div className="space-y-5" data-testid="comparatifs-tab">
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-1 rounded-full bg-slate-100 p-1" data-testid="group-by-switch">
                    {[
                        { k: "offer", l: "Par offre" },
                        { k: "source", l: "Par source" },
                        { k: "recruteur", l: "Par chargé·e" },
                    ].map((g) => (
                        <button key={g.k} onClick={() => setGroupBy(g.k)}
                            data-testid={`group-by-${g.k}`}
                            className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition ${
                                groupBy === g.k ? "bg-shariti-black text-white" : "text-slate-500 hover:text-shariti-black"
                            }`}>
                            {g.l}
                        </button>
                    ))}
                </div>
                <div className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-3" data-testid="thresholds-legend">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> OK</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Attention</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-400" /> Alerte</span>
                    <span className="ml-2 text-slate-300">· Seuils : Paramètres → Analytics</span>
                </div>
            </div>

            <div className="rounded-2xl border-2 border-slate-100 bg-white overflow-x-auto" data-testid="comparatifs-table">
                {/* iter89 · Bandeau "Décision RH" quand une meilleure source
                    est identifiée. Uniquement en mode `group_by=source`
                    (pour les autres dimensions, le concept "meilleure" n'a
                    pas la même valeur métier — cf. CEO 2026-09-01). */}
                {groupBy === "source" && data.best_key && (() => {
                    const best = rows.find(r => r.key === data.best_key);
                    if (!best) return null;
                    return (
                        <div
                            className="border-b-2 border-emerald-200 bg-emerald-50/70 px-4 py-3 flex items-start gap-3"
                            data-testid="best-source-recommendation"
                        >
                            <div className="text-lg leading-none pt-0.5">🏆</div>
                            <div className="text-[12px] leading-relaxed text-emerald-900">
                                <div className="font-black">
                                    Décision RH — Meilleure source sur la période : <span className="underline">{best.label}</span>
                                </div>
                                <div className="text-[11px] text-emerald-800/80 mt-0.5">
                                    Closing global {best.closing_rate}% sur {best.volume} appels ({best.unique_candidates} candidats).
                                    Recommandation : injecter davantage de budget candidatures sur cette source.
                                </div>
                            </div>
                        </div>
                    );
                })()}
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                            {columns.map((c) => (
                                <th key={c.key} onClick={() => onSort(c.key)}
                                    className={`px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-500 cursor-pointer hover:text-shariti-black ${
                                        c.align === "right" ? "text-right" : "text-left"
                                    }`}
                                    data-testid={`sort-${c.key}`}>
                                    {c.label}
                                    {sortKey === c.key && (sortDir === "asc" ? " ↑" : " ↓")}
                                </th>
                            ))}
                            <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-widest text-slate-500">Alerte</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={columns.length + 1} className="text-center py-10 text-slate-400 text-sm">
                                    Aucune ligne à comparer sur la période
                                </td>
                            </tr>
                        )}
                        {rows.map((r) => (
                            <tr
                                key={`${groupBy}-${r.key}`}
                                className={`border-b border-slate-50 last:border-b-0 hover:bg-slate-50/50 ${r.is_best ? "bg-emerald-50/40" : ""}`}
                                data-testid={`compare-row-${r.key}`}
                            >
                                <td className="px-4 py-3 text-sm font-bold text-shariti-black truncate max-w-[280px]">
                                    <div className="flex items-center gap-2">
                                        {r.is_best && (
                                            <span
                                                className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-800 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest"
                                                title="Meilleure source pour la période — décision RH : réinjecter du budget ici"
                                                data-testid={`best-source-badge-${r.key}`}
                                            >
                                                🏆 Meilleure
                                            </span>
                                        )}
                                        <span className="truncate">{r.label}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-sm font-black text-right">
                                    {r.volume}
                                    {r.unique_candidates > 0 && (
                                        <div className="text-[10px] font-medium text-slate-400">
                                            {r.unique_candidates} cand.
                                        </div>
                                    )}
                                </td>
                                <td className={`px-4 py-3 text-sm font-black text-right ${r.reach_rate < data.thresholds.reach_rate_red ? "text-rose-600" : r.reach_rate < data.thresholds.reach_rate_orange ? "text-amber-600" : "text-emerald-600"}`}>
                                    {r.reach_rate}%
                                    <div className="text-[10px] font-medium text-slate-400">
                                        {r.reached} / {r.volume}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-right text-slate-600">
                                    {r.complete_rate}%
                                    {r.reached > 0 && (
                                        <div className="text-[10px] font-medium text-slate-400">
                                            {r.completed} / {r.reached}
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-right text-slate-600">{r.qualify_rate}%</td>
                                <td className={`px-4 py-3 text-sm font-black text-right ${r.closing_rate < data.thresholds.closing_rate_red ? "text-rose-600" : r.closing_rate < data.thresholds.closing_rate_orange ? "text-amber-600" : "text-emerald-600"}`}>{r.closing_rate}%</td>
                                <td className="px-4 py-3 text-right">
                                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest border ${toneClasses[r.alert_level]}`} data-testid={`alert-${r.alert_level}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${r.alert_level === "green" ? "bg-emerald-500" : r.alert_level === "orange" ? "bg-amber-500" : "bg-rose-500"}`} />
                                        {r.alert_level === "green" ? "OK" : r.alert_level === "orange" ? "Attention" : "Alerte"}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <AiCommentaryBlock scope="comparatifs" filters={{ ...filters, group_by: groupBy }} testid="ai-commentary-comparatifs" />
        </div>
    );
}
